import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { Filters, mergeFilters } from "@/lib/filters";
import { FetchListRequest } from "@/services/api";
import { ProjectTypesService } from "@/services/projectType";
import { ReadProjectType } from "@/types/projectType";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const useProjectTypesQuery = ({
  name = "projectTypes",
  enabled = true,
  filters: filtersFromProps,
  defaultSorting,
}: {
  name?: string;
  enabled?: boolean;
  filters?: Filters<ReadProjectType>;
  defaultSorting?: { id: string; desc: boolean };
} = {}) => {
  const options = useCreateDataTableOptions<ReadProjectType>(name);
  const fetchList = (fetchParams: FetchListRequest<ReadProjectType>) => {
    return ProjectTypesService.getList(fetchParams);
  };

  const filters = useMemo(
    () => mergeFilters(options.filters, filtersFromProps),
    [filtersFromProps, options.filters],
  );

  const queryKey = useDynamicQueryParams(name, options);
  const sorting = useMemo(() => {
    return options.sorting[0] || defaultSorting;
  }, [options.sorting, defaultSorting]);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchList({
        lastItem: pageParam,
        filters,
        sorting,
      }),
    initialPageParam: undefined,
    getNextPageParam: ({ last_item }) => last_item,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled,
  });

  return {
    query,
    options,
  };
};
